import { motion } from 'framer-motion';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Avatar, Box, Button, CheckOutline, Flex, Text } from '@bonusly/design-system';

/** Wire shape for one entry in a meeting_people_card payload. Keys are
 *  camelCase at the JS boundary (matching Ruby's `to_h`). Every field is
 *  optional so malformed data never breaks a render — missing values
 *  degrade visually but do not crash. */
export interface MeetingPerson {
	userId?: string;
	name?: string;
	username?: string;
	avatarUrl?: string | null;
	participantKey?: string;
	subtitle?: string | null;
	cta?: {
		label?: string;
		href?: string;
	};
	/** Server-supplied completion status. "completed" renders the green
	 *  state; anything else falls through to the default Create CTA. */
	status?: string | null;
}

export interface BizyMeetingPeopleCardProps {
	/** Optional row title. Rendered above the scroll container. */
	title?: string;
	/** The people to render. Empty / missing lists suppress the whole card. */
	people: MeetingPerson[];
}

/**
 * Generic horizontal row of per-person cards used by the
 * `suggest_people_to_meet` tool (and, in future, other tools that surface
 * a list of people with per-item CTAs — e.g. "What are my upcoming meetings?").
 *
 * Every card is data-driven from `cta.label`, `cta.href`, `subtitle`, and
 * `status`. The component has no knowledge of "Create" vs "View" semantics —
 * that lives in the tool/orchestrator that produced the payload.
 *
 * Click handling: the CTA uses `<RouterLink to>` for SPA navigation so the
 * Bizy side panel (outside the main outlet) stays mounted. Local
 * `completedLocally` state therefore survives Create → /meet/with/:key →
 * back-navigation, giving an instant "Created" stamp without waiting for the
 * server. RouterLink also preserves middle/cmd/right-click "open in new tab";
 * the click handler only updates local completion state — it must not call
 * `navigate()`, or the link would push the route twice (double Back).
 *
 * Tradeoff (v1 scope): cross-session persistence of per-person "completed"
 * is NOT plumbed through `nodeChoices` (which is keyed per-card, not
 * per-person). On a fresh session the card re-renders from server state.
 * Acceptable because the orchestrator filters out existing 1:1 partners
 * on every fresh tool call — so a once-clicked person should not reappear
 * in a later turn. Adding per-person `nodeChoices` keying would require
 * widening the upstream Record type and isn't worth the blast radius for v1.
 */
export function BizyMeetingPeopleCard({ title, people }: BizyMeetingPeopleCardProps) {
	const { t } = useTranslation('admin_bizy');
	const [completedLocally, setCompletedLocally] = useState<Record<number, boolean>>({});

	const hasPeople = Array.isArray(people) && people.length > 0;
	if (!hasPeople) return null;

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box my={2}>
				{title && (
					<Text fontSize="xs" fontWeight="semibold" color="text.softer" textTransform="uppercase" letterSpacing="wider" mb={2}>
						{title}
					</Text>
				)}

				<Flex gap={2} overflowX="auto" pb={1}>
					{people.map((person, index) => {
						const isCompleted = person.status === 'completed' || completedLocally[index] === true;
						return (
							<PersonCard
								key={`${person.participantKey ?? person.userId ?? index}`}
								person={person}
								isCompleted={isCompleted}
								completedLabel={t('meeting_people_card.completed')}
								onCtaClick={() => {
									if (isCompleted || !person.cta?.href) return;
									setCompletedLocally((prev) => ({ ...prev, [index]: true }));
								}}
							/>
						);
					})}
				</Flex>
			</Box>
		</motion.div>
	);
}

interface PersonCardProps {
	person: MeetingPerson;
	isCompleted: boolean;
	completedLabel: string;
	onCtaClick: () => void;
}

function PersonCard({ person, isCompleted, completedLabel, onCtaClick }: PersonCardProps) {
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			// Modifier-clicks (cmd/ctrl/shift/middle) preserve their browser-native
			// "open in new tab/window" behavior — let the RouterLink handle them.
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
				return;
			}
			// For plain left-clicks: stamp "completed" then let RouterLink navigate.
			// We don't preventDefault — the RouterLink's own SPA navigation runs next.
			onCtaClick();
		},
		[onCtaClick]
	);

	const ctaLabel = person.cta?.label ?? '';
	const href = person.cta?.href;

	return (
		<Box
			flexShrink={0}
			w="140px"
			border="1px solid"
			borderColor={isCompleted ? 'success.border' : 'border.soft'}
			bg={isCompleted ? 'success.bg' : 'bg.surface'}
			borderRadius={12}
			px={3}
			py={3}
			transition="background 0.2s, border-color 0.2s"
		>
			<Flex direction="column" align="center" gap={2}>
				<Avatar name={person.name ?? person.username ?? ''} src={person.avatarUrl ?? undefined} size="md" />

				<Flex direction="column" align="center" gap={0.5} w="full" minW={0}>
					<Text fontSize="sm" fontWeight="semibold" color="text.default" textAlign="center" noOfLines={1} w="full">
						{person.name}
					</Text>
					{person.username && (
						<Text fontSize="xs" color="text.softest" textAlign="center" noOfLines={1} w="full">
							@{person.username}
						</Text>
					)}
					{person.subtitle && (
						<Text fontSize="xs" color="text.soft" textAlign="center" noOfLines={1} w="full" mt={0.5}>
							{person.subtitle}
						</Text>
					)}
				</Flex>

				{isCompleted ? (
					<Flex align="center" justify="center" gap={1} w="full" px={2} py={1.5} borderRadius={8} bg="success.bg">
						<CheckOutline boxSize={3} color="success.text" />
						<Text fontSize="xs" fontWeight="medium" color="success.text">
							{completedLabel}
						</Text>
					</Flex>
				) : href ? (
					<Button as={RouterLink} to={href} size="sm" variant="alpha" colorScheme="primary" w="full" onClick={handleClick}>
						{ctaLabel}
					</Button>
				) : (
					<Button size="sm" variant="alpha" colorScheme="primary" w="full" isDisabled>
						{ctaLabel}
					</Button>
				)}
			</Flex>
		</Box>
	);
}
